import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiCircle, FiDownload, FiExternalLink } = FiIcons;

/**
 * Displays event resources with completion checkboxes and progress bar.
 * Calls onToggleComplete when user marks a resource complete/incomplete.
 */
export default function ResourceProgressTracker({
  resources = [],
  resourceProgress = {},
  progressPercentage = 0,
  completedCount = 0,
  totalCount = 0,
  onToggleComplete,
  loading = false
}) {
  const total = totalCount || resources.length;
  const completed = completedCount ?? Object.values(resourceProgress).filter((p) => p?.is_completed).length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-2 bg-slate-200 rounded-full w-3/4" />
        <div className="h-10 bg-slate-100 rounded" />
        <div className="h-10 bg-slate-100 rounded" />
      </div>
    );
  }

  if (!resources.length) {
    return (
      <div className="text-sm text-slate-500 py-4">
        No resources for this event yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span className="text-slate-600">Progress</span>
          <span className="font-medium text-slate-900">
            {completed} / {total} completed ({pct}%)
          </span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#00AAFF] rounded-full transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <ul className="space-y-2">
        {resources.map((resource) => {
          const progress = resourceProgress[resource.id];
          const isCompleted = progress?.is_completed ?? false;

          return (
            <li
              key={resource.id}
              className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50/50"
            >
              <button
                type="button"
                onClick={() => onToggleComplete?.(resource.id, !isCompleted)}
                className="flex-shrink-0 text-slate-400 hover:text-[#00AAFF] focus:outline-none"
                aria-label={isCompleted ? 'Mark incomplete' : 'Mark complete'}
              >
                {isCompleted ? (
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-emerald-600" />
                ) : (
                  <SafeIcon icon={FiCircle} className="w-6 h-6" />
                )}
              </button>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-900">{resource.title}</p>
                {resource.description && (
                  <p className="text-sm text-slate-500 line-clamp-1">{resource.description}</p>
                )}
              </div>
              {resource.file_url && (
                <a
                  href={resource.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 inline-flex items-center gap-1 text-sm text-[#00AAFF] hover:underline"
                >
                  <SafeIcon icon={resource.resource_type === 'link' ? FiExternalLink : FiDownload} className="w-4 h-4" />
                  {resource.resource_type === 'link' ? 'Open' : 'Download'}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
